/**
 * Modal layout parts — trivial composition wrappers used by feature modals.
 *
 * Upstream `@archieai/ui` Modal exposes a single composite `Modal` component;
 * these helpers preserve the call-site shape (`<ModalHeader title />`,
 * `<ModalBody>`, `<ModalFooter align="right">`) that local feature modals
 * have always used. They render brand-aware tokens (`text-text`, `text-muted`,
 * `border-line`) so the Martha ↔ Archie brand toggle works automatically.
 *
 * These are NOT primitives — they are thin layout containers. If a future
 * Archie UI feature needs a true `ModalHeader`/`ModalFooter` primitive, it
 * should be authored upstream and these helpers retired.
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface ModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  /** Reserved for future close-button slot; currently a no-op (upstream Modal renders its own close button). */
  onClose?: () => void;
}

export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, title, subtitle, onClose: _onClose, children, ...props }, ref) => (
    <div ref={ref} className={clsx('flex flex-col gap-1', className)} {...props}>
      {title && <h2 className="text-xl font-semibold text-text pr-8">{title}</h2>}
      {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
      {children}
    </div>
  ),
);
ModalHeader.displayName = 'ModalHeader';

export const ModalBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx('w-full', className)} {...props}>
      {children}
    </div>
  ),
);
ModalBody.displayName = 'ModalBody';

export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right';
}

export const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, align = 'right', children, ...props }, ref) => {
    const alignClass =
      align === 'left' ? 'justify-start' : align === 'center' ? 'justify-center' : 'justify-end';
    return (
      <div
        ref={ref}
        className={clsx(
          'flex items-center gap-3 pt-4 mt-2 border-t border-line',
          alignClass,
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
ModalFooter.displayName = 'ModalFooter';
